import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { I18nProvider } from "@/context/I18nContext";
import { BrandProvider } from "@/context/BrandContext";
import { DevFieldNotesProvider } from "@/context/DevFieldNotesContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Shell } from "@/components/Shell";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/Home";
import DealersList from "@/pages/DealersList";
import BulkUpdateDealers from "@/pages/BulkUpdateDealers";
import MyDealership from "@/pages/MyDealership";
import LegalPages from "@/pages/LegalPages";
import LegalHtmlEditor from "@/pages/LegalHtmlEditor";
import DepartmentsAdmin from "@/pages/DepartmentsAdmin";
import Settings from "@/pages/Settings";
import Imports from "@/pages/Imports";
import Profile from "@/pages/Profile";
import AuditLog from "@/pages/AuditLog";
import ApiLogs from "@/pages/ApiLogs";
import Activations from "@/pages/Activations";
import ActivationDetail from "@/pages/ActivationDetail";
import Users from "@/pages/Users";
import MyDealerships from "@/pages/MyDealerships";
import DnsSetup from "@/pages/dns/DnsSetup";
import { PERM } from "@/lib/permissions";
import "@/App.css";

// Routes gate on a permission, never a role list — which role happens to
// grant it lives in backend/models.py's ROLE_PERMISSIONS and nowhere else.
// See docs/developer/13-authorization.md.

function ShellRoute({ children }) {
  return (
    <ProtectedRoute>
      <Shell>{children}</Shell>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrandProvider>
        <ThemeProvider>
          <I18nProvider>
            <AuthProvider>
              <DevFieldNotesProvider>
              <BrowserRouter>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />

                  <Route path="/" element={<ShellRoute><Home /></ShellRoute>} />

                  <Route
                    path="/dealers"
                    element={
                      <ProtectedRoute permission={PERM.NETWORK_DEALERS_READ}>
                        <Shell>
                          <DealersList />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dealers/bulk-update"
                    element={
                      <ProtectedRoute permission={PERM.NETWORK_DEALERS_READ}>
                        <Shell>
                          <BulkUpdateDealers />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dealers/:id"
                    element={
                      <ProtectedRoute permission={PERM.NETWORK_DEALERS_READ}>
                        <Shell>
                          <MyDealership mode="byId" />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dealers/:id/legal-pages"
                    element={
                      <ProtectedRoute permission={PERM.NETWORK_DEALERS_READ}>
                        <Shell>
                          <LegalPages mode="byId" />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/my-dealership"
                    element={
                      <ShellRoute>
                        <MyDealership mode="self" />
                      </ShellRoute>
                    }
                  />

                  <Route
                    path="/legal-pages"
                    element={
                      <ShellRoute>
                        <LegalPages mode="self" />
                      </ShellRoute>
                    }
                  />

                  <Route
                    path="/my-dealerships"
                    element={
                      <ShellRoute>
                        <MyDealerships />
                      </ShellRoute>
                    }
                  />

                  <Route
                    path="/my-dealerships/:dealerId"
                    element={
                      <ShellRoute>
                        <MyDealership mode="mine" />
                      </ShellRoute>
                    }
                  />

                  <Route
                    path="/my-dealerships/:dealerId/legal-pages"
                    element={
                      <ShellRoute>
                        <LegalPages mode="mine" />
                      </ShellRoute>
                    }
                  />

                  <Route
                    path="/legal-html-editor"
                    element={
                      <ProtectedRoute permission={PERM.LEGAL_TEMPLATES_MANAGE}>
                        <Shell>
                          <LegalHtmlEditor />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/departments"
                    element={
                      <ProtectedRoute permission={PERM.DEPARTMENTS_MANAGE}>
                        <Shell>
                          <DepartmentsAdmin />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute permission={PERM.SYSTEM_SETTINGS_MANAGE}>
                        <Shell>
                          <Settings />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/imports"
                    element={
                      <ProtectedRoute permission={PERM.IMPORTS_RUN}>
                        <Shell>
                          <Imports />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/activations"
                    element={
                      <ProtectedRoute permission={PERM.ACTIVATIONS_MANAGE}>
                        <Shell><Activations /></Shell>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/activations/:id"
                    element={
                      <ProtectedRoute permission={PERM.ACTIVATIONS_MANAGE}>
                        <Shell><ActivationDetail /></Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/audit-log"
                    element={
                      <ProtectedRoute permission={PERM.SYSTEM_AUDIT_READ}>
                        <Shell>
                          <AuditLog />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/api-logs"
                    element={
                      <ProtectedRoute permission={PERM.SYSTEM_AUDIT_READ}>
                        <Shell>
                          <ApiLogs />
                        </Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute permission={PERM.USERS_MANAGE}>
                        <Shell><Users /></Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dns-setup"
                    element={
                      <ProtectedRoute permission={PERM.DNS_MANAGE}>
                        <Shell><DnsSetup /></Shell>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/profile" element={<ShellRoute><Profile /></ShellRoute>} />

                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster richColors position="top-right" />
              </BrowserRouter>
              </DevFieldNotesProvider>
            </AuthProvider>
          </I18nProvider>
        </ThemeProvider>
      </BrandProvider>
    </div>
  );
}
